import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const BASE_URL = "https://www.assocham.org/assocham_backend";
const API_URL = `${BASE_URL}/api/get-international-event.php`;
const EVENTS_PER_PAGE = 10;
const SEARCH_DELAY_MS = 3000;

const INITIAL_FILTERS = { tab: "forthcoming", eventType: "", subject: "" };

function getEventImage(event) {
  const image = event.image;
  if (!image) return `${BASE_URL}/assets/images/events-banner-main.jpg`;
  if (image.startsWith("http")) return image;
  if (image.startsWith("/")) return `${BASE_URL}${image}`;
  if (image.startsWith("assets/")) return `${BASE_URL}/${image}`;
  return `${BASE_URL}/assets/images/${image}`;
}

function EventImageCard({ event, active }) {
  const date = event.date || "01 Jan, 2026";
  const match = date.match(/(\d{1,2})\s+([A-Za-z]{3})[^\d]*(20\d{2})/);
  const day = match?.[1] || "01";
  const month = match?.[2] || "JAN";
  const year = match?.[3] || "2026";
  return (
    <div className="relative mx-auto h-[140px] w-[255px] shrink-0 overflow-hidden rounded-2xl shadow-sm sm:mx-0">
      <img
        src={getEventImage(event)}
        alt={event.title}
        className="absolute inset-0 h-full w-full object-cover"
        onError={(e) => {
          e.currentTarget.src = `${BASE_URL}/assets/images/events-banner-main.jpg`;
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
      <span className="absolute right-2 top-2 rounded-md bg-black/45 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">
        {event.type || "Summit"}
      </span>
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between px-3 py-2.5">
        <div>
          <p className="text-2xl font-bold leading-none text-white">{day}</p>
          <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-300">{month}</p>
        </div>
        <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white ${active ? "bg-red-700" : "bg-gray-600"}`}>
          {year}
        </span>
      </div>
    </div>
  );
}

function EventLink({ event, className, children }) {
  const isExternal = Number(event.externalIndicator) === 1 && event.externalUrl;
  if (isExternal) {
    const externalUrl = event.externalUrl.startsWith("http") ? event.externalUrl : `${BASE_URL}/${event.externalUrl}`;
    return (
      <a href={externalUrl} target="_blank" rel="noreferrer" className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={`/event-details/${event.slug}`} className={className}>
      {children}
    </Link>
  );
}

function EventCard({ event, active }) {
  return (
    <article className="group rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row">
        <EventLink event={event} className="shrink-0">
          <EventImageCard event={event} active={active} />
        </EventLink>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase text-red-700">
              {event.category || "General"}
            </span>
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
              {event.accent || "Industry"}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
              }`}
            >
              {active ? "Upcoming" : "Archived"}
            </span>
          </div>
          <h3 className="text-base font-semibold leading-snug text-gray-900 group-hover:text-red-700">
            <EventLink event={event} className="hover:underline">
              {event.title}
            </EventLink>
          </h3>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-medium text-gray-500">📅 {event.date}</p>
            <EventLink
              event={event}
              className="inline-flex w-fit items-center gap-2 rounded-lg bg-red-700 px-3.5 py-2 text-[13px] font-semibold text-white no-underline hover:bg-red-800"
            >
              {active ? "Register Interest" : "View Highlights"} <span>→</span>
            </EventLink>
          </div>
        </div>
      </div>
    </article>
  );
}

function SelectField({ label, value, options, onChange, placeholder }) {
  return (
    <label className="block space-y-1 text-xs font-medium text-gray-600">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 outline-none focus:border-red-500"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function Filters({ filters, updateFilters, keywordInput, setKeywordInput, pending, search, clear, eventTypes, subjects, onDone, resultCount }) {
  return (
    <>
      <div className="space-y-3">
        <label className="block space-y-1 text-xs font-medium text-gray-600">
          Keywords
          <input
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search by title, sector…"
            className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-red-500"
          />
        </label>
        <div>
          <p className="mb-1 text-xs font-medium text-gray-600">Status</p>
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => updateFilters({ tab: "forthcoming" })}
              className={`flex-1 rounded-md py-1.5 text-sm ${filters.tab === "forthcoming" ? "bg-red-700 text-white" : "text-gray-500"}`}
            >
              Forthcoming
            </button>
            <button
              onClick={() => updateFilters({ tab: "past" })}
              className={`flex-1 rounded-md py-1.5 text-sm ${filters.tab === "past" ? "bg-red-700 text-white" : "text-gray-500"}`}
            >
              Past
            </button>
          </div>
        </div>
        <SelectField
          label="Event type"
          value={filters.eventType}
          options={eventTypes}
          onChange={(value) => updateFilters({ eventType: value })}
          placeholder="All types"
        />
        <SelectField
          label="Subject"
          value={filters.subject}
          options={subjects}
          onChange={(value) => updateFilters({ subject: value })}
          placeholder="All subjects"
        />
      </div>
      <div className="mt-5 flex gap-2">
        {pending && (
          <button onClick={search} className="flex-1 rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white hover:bg-red-800">
            Search
          </button>
        )}
        <button onClick={clear} className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
          Clear
        </button>
      </div>
      {onDone && (
        <button onClick={onDone} className="mt-3 w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white">
          Show results ({resultCount})
        </button>
      )}
    </>
  );
}

function Pagination({ page, totalPages, onChange }) {
  const pages = (() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, 4, "dots", totalPages];
    if (page >= totalPages - 2) return [1, "dots", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "dots-left", page - 1, page, page + 1, "dots-right", totalPages];
  })();
  return (
    <nav className="flex flex-wrap items-center justify-center gap-2 pt-4">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-40"
      >
        Previous
      </button>
      {pages.map((item, index) =>
        typeof item === "string" ? (
          <span key={`${item}-${index}`} className="px-1 font-bold text-gray-400">
            ...
          </span>
        ) : (
          <button
            key={item}
            onClick={() => onChange(item)}
            className={`flex h-10 min-w-10 items-center justify-center rounded-lg px-3 text-sm font-bold ${
              item === page ? "bg-red-700 text-white" : "border border-gray-200 bg-white text-gray-600 hover:text-red-700"
            }`}
          >
            {item}
          </button>
        )
      )}
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-40"
      >
        Next
      </button>
    </nav>
  );
}

export default function InternationalEvent() {
  // Server pehle se sirf is_international = 1 wale events bhejta hai
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Tab / select filters turant apply hote hain
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  // Keyword: keywordInput = jo type ho raha hai, keywords = jo actually apply hai
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState("");

  useEffect(() => {
    fetch(API_URL)
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || "Could not load events.");
        return json.data || [];
      })
      .then(setAllEvents)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Typing rukne ke 3 sec baad keyword auto apply
  useEffect(() => {
    const trimmed = keywordInput.trim();
    if (trimmed === keywords) return;
    const timer = setTimeout(() => {
      setKeywords(trimmed);
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [keywordInput, keywords]);

  const pending = keywordInput.trim() !== keywords;

  const updateFilters = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const search = () => {
    setKeywords(keywordInput.trim());
    setPage(1);
  };

  const clear = () => {
    setFilters(INITIAL_FILTERS);
    setKeywordInput("");
    setKeywords("");
    setPage(1);
  };

  const eventTypes = useMemo(() => [...new Set(allEvents.map((e) => e.type).filter(Boolean))], [allEvents]);
  const subjects = useMemo(() => [...new Set(allEvents.map((e) => e.accent).filter(Boolean))], [allEvents]);

  const events = useMemo(() => {
    const q = keywords.toLowerCase();
    return allEvents.filter((e) => {
      if (e.status !== filters.tab) return false;
      if (filters.eventType && e.type !== filters.eventType) return false;
      if (filters.subject && e.accent !== filters.subject) return false;
      if (!q) return true;
      return `${e.title} ${e.category} ${e.accent}`.toLowerCase().includes(q);
    });
  }, [allEvents, filters, keywords]);

  const totalPages = Math.max(1, Math.ceil(events.length / EVENTS_PER_PAGE));
  const pageEvents = useMemo(() => events.slice((page - 1) * EVENTS_PER_PAGE, page * EVENTS_PER_PAGE), [events, page]);

  const changePage = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    document.getElementById("international-events")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const filterProps = { filters, updateFilters, keywordInput, setKeywordInput, pending, search, clear, eventTypes, subjects };

  return (
    <section id="international-events" className="scroll-mt-24">
      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="hidden h-fit rounded-2xl border border-gray-200 bg-white p-5 shadow-sm lg:sticky lg:top-5 lg:block">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Filter by</p>
          <h2 className="mb-4 text-lg font-bold">Events Search</h2>
          <Filters {...filterProps} />
        </aside>

        <main className="space-y-5">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-red-700">International Events</p>
                <h2 className="mt-1 text-2xl font-bold">{filters.tab === "forthcoming" ? "Forthcoming Events" : "Past Events"}</h2>
              </div>
              <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                <button
                  onClick={() => updateFilters({ tab: "forthcoming" })}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium ${filters.tab === "forthcoming" ? "bg-red-700 text-white" : "text-gray-500"}`}
                >
                  Forthcoming
                </button>
                <button
                  onClick={() => updateFilters({ tab: "past" })}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium ${filters.tab === "past" ? "bg-red-700 text-white" : "text-gray-500"}`}
                >
                  Past
                </button>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <p className="text-lg font-bold">Events ({loading ? "..." : events.length})</p>
                <p className="text-sm text-gray-500">
                  {loading
                    ? "Loading events..."
                    : `Showing ${events.length ? (page - 1) * EVENTS_PER_PAGE + 1 : 0}–${Math.min(page * EVENTS_PER_PAGE, events.length)} of ${events.length} results`}
                </p>
              </div>
              <button onClick={() => setFilterOpen(true)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold lg:hidden">
                Filters
              </button>
            </div>
          </div>

          {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

          <div className="grid gap-3">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-44 animate-pulse rounded-2xl bg-gray-200" />)
              : pageEvents.map((event) => <EventCard key={event.id} event={event} active={filters.tab === "forthcoming"} />)}
          </div>

          {!loading && !error && events.length === 0 && (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
              <h3 className="font-bold">No matching events</h3>
              <button onClick={clear} className="mt-4 rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white">
                Reset filters
              </button>
            </div>
          )}

          {!loading && events.length > EVENTS_PER_PAGE && <Pagination page={page} totalPages={totalPages} onChange={changePage} />}
        </main>
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 p-4 lg:hidden">
          <div className="ml-auto h-full max-w-sm overflow-y-auto rounded-2xl bg-white p-5">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold">Events Search</h2>
              <button onClick={() => setFilterOpen(false)} className="text-2xl">
                ×
              </button>
            </div>
            <Filters
              {...filterProps}
              search={() => {
                search();
                setFilterOpen(false);
              }}
              onDone={() => {
                if (pending) search();
                setFilterOpen(false);
              }}
              resultCount={events.length}
            />
          </div>
        </div>
      )}
    </section>
  );
}